/**
 * Named scenario library for admin Economics / Forecast.
 * Stored in localStorage (not website DB). Export/import for backup + MCP bridge.
 */

export const LIBRARY_KEYS = {
  economics: 'ffc.admin.economics.library.v1',
  forecast: 'ffc.admin.forecast.library.v1',
};

export const ACTIVE_META_KEYS = {
  economics: 'ffc.admin.economics.activeMeta.v1',
  forecast: 'ffc.admin.forecast.activeMeta.v1',
};

function slugify(name) {
  return String(name || 'scenario')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'scenario';
}

function newId(name) {
  return `${slugify(name)}-${Date.now().toString(36)}`;
}

/**
 * @param {'economics'|'forecast'} kind
 * @returns {{ version: number, entries: object[] }}
 */
export function loadLibrary(kind) {
  const key = LIBRARY_KEYS[kind];
  if (!key) return { version: 1, entries: [] };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { version: 1, entries: [] };
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.entries)) return { version: 1, entries: [] };
    return { version: 1, entries: parsed.entries };
  } catch {
    return { version: 1, entries: [] };
  }
}

function persistLibrary(kind, library) {
  localStorage.setItem(LIBRARY_KEYS[kind], JSON.stringify(library));
}

/**
 * @param {'economics'|'forecast'} kind
 */
export function loadActiveMeta(kind) {
  try {
    const raw = localStorage.getItem(ACTIVE_META_KEYS[kind]);
    if (!raw) return { libraryId: null, libraryName: null };
    const parsed = JSON.parse(raw);
    return {
      libraryId: parsed.libraryId || null,
      libraryName: parsed.libraryName || null,
    };
  } catch {
    return { libraryId: null, libraryName: null };
  }
}

export function persistActiveMeta(kind, meta) {
  localStorage.setItem(
    ACTIVE_META_KEYS[kind],
    JSON.stringify({
      libraryId: meta?.libraryId || null,
      libraryName: meta?.libraryName || null,
    })
  );
}

/**
 * List entries newest-first (metadata only).
 * @param {'economics'|'forecast'} kind
 */
export function listLibraryEntries(kind) {
  const { entries } = loadLibrary(kind);
  return entries
    .map(({ id, name, notes, createdAt, updatedAt }) => ({
      id,
      name,
      notes: notes || '',
      createdAt,
      updatedAt,
    }))
    .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
}

/**
 * @param {'economics'|'forecast'} kind
 * @param {string} id
 */
export function getLibraryEntry(kind, id) {
  return loadLibrary(kind).entries.find((e) => e.id === id) || null;
}

/**
 * Save or overwrite by id. If id omitted, creates new.
 * @param {'economics'|'forecast'} kind
 * @param {{ id?: string, name: string, notes?: string, payload: object }} input
 */
export function saveLibraryEntry(kind, input) {
  const name = (input.name || '').trim();
  if (!name) throw new Error('Name is required.');
  if (!input.payload || typeof input.payload !== 'object') {
    throw new Error('Payload is required.');
  }

  const library = loadLibrary(kind);
  const now = new Date().toISOString();
  const existingIdx = input.id
    ? library.entries.findIndex((e) => e.id === input.id)
    : -1;

  let entry;
  if (existingIdx >= 0) {
    entry = {
      ...library.entries[existingIdx],
      name,
      notes: input.notes ?? library.entries[existingIdx].notes ?? '',
      updatedAt: now,
      payload: JSON.parse(JSON.stringify(input.payload)),
    };
    library.entries[existingIdx] = entry;
  } else {
    entry = {
      id: input.id || newId(name),
      name,
      notes: input.notes || '',
      createdAt: now,
      updatedAt: now,
      kind,
      payload: JSON.parse(JSON.stringify(input.payload)),
    };
    library.entries.push(entry);
  }

  persistLibrary(kind, library);
  persistActiveMeta(kind, { libraryId: entry.id, libraryName: entry.name });
  return entry;
}

/**
 * @param {'economics'|'forecast'} kind
 * @param {string} id
 */
export function deleteLibraryEntry(kind, id) {
  const library = loadLibrary(kind);
  const next = library.entries.filter((e) => e.id !== id);
  persistLibrary(kind, { version: 1, entries: next });
  const meta = loadActiveMeta(kind);
  if (meta.libraryId === id) {
    persistActiveMeta(kind, { libraryId: null, libraryName: null });
  }
  return { deleted: library.entries.length - next.length };
}

/**
 * Rename / update notes without changing payload.
 */
export function updateLibraryEntryMeta(kind, id, { name, notes }) {
  const library = loadLibrary(kind);
  const idx = library.entries.findIndex((e) => e.id === id);
  if (idx < 0) throw new Error('Entry not found.');
  const now = new Date().toISOString();
  const entry = {
    ...library.entries[idx],
    name: name != null ? String(name).trim() || library.entries[idx].name : library.entries[idx].name,
    notes: notes != null ? notes : library.entries[idx].notes,
    updatedAt: now,
  };
  library.entries[idx] = entry;
  persistLibrary(kind, library);
  const meta = loadActiveMeta(kind);
  if (meta.libraryId === id) {
    persistActiveMeta(kind, { libraryId: id, libraryName: entry.name });
  }
  return entry;
}

/**
 * Full library export for backup / MCP bridge.
 */
export function exportLibraryBundle(kind) {
  return {
    kind,
    exportedAt: new Date().toISOString(),
    ...loadLibrary(kind),
  };
}

/**
 * Merge or replace library from imported bundle.
 * @param {'economics'|'forecast'} kind
 * @param {object} bundle
 * @param {'merge'|'replace'} mode
 */
export function importLibraryBundle(kind, bundle, mode = 'merge') {
  if (!bundle || bundle.kind !== kind) {
    throw new Error(`Bundle kind mismatch — expected ${kind}.`);
  }
  const incoming = Array.isArray(bundle.entries) ? bundle.entries : [];
  if (mode === 'replace') {
    persistLibrary(kind, { version: 1, entries: incoming });
    return { count: incoming.length, mode };
  }
  const library = loadLibrary(kind);
  const byId = new Map(library.entries.map((e) => [e.id, e]));
  for (const entry of incoming) {
    if (!entry?.id || !entry?.payload) continue;
    byId.set(entry.id, entry);
  }
  const entries = [...byId.values()];
  persistLibrary(kind, { version: 1, entries });
  return { count: entries.length, mode };
}
